import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/utils";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const emptyForm = (fields) => {
  const f = {};
  fields.forEach((fl) => {
    f[fl.key] = fl.type === "checkbox" ? (fl.default ?? true) : fl.type === "number" ? (fl.default ?? 0) : (fl.default ?? "");
  });
  return f;
};

const EntityManager = ({ endpoint, title, fields, columns }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(fields));
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios
      .get(`${API}/admin/${endpoint}`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erreur de chargement"));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm(fields));
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    const f = {};
    fields.forEach((fl) => (f[fl.key] = item[fl.key] ?? emptyForm(fields)[fl.key]));
    setForm(f);
    setOpen(true);
  };

  const setVal = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((fl) => {
        if (fl.type === "number") payload[fl.key] = Number(payload[fl.key]) || 0;
      });
      if (editingId) await axios.put(`${API}/admin/${endpoint}/${editingId}`, payload);
      else await axios.post(`${API}/admin/${endpoint}`, payload);
      toast.success(editingId ? "Modifié" : "Ajouté");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString() || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await axios.delete(`${API}/admin/${endpoint}/${id}`);
      toast.success("Supprimé");
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid={`admin-entity-${endpoint}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="hd-italic text-3xl text-forest">{title}</h2>
        <button onClick={openNew} data-testid={`admin-add-${endpoint}`} className="btn-primary">
          <Plus size={16} /> Ajouter
        </button>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm">
          <thead className="bg-bone">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-4 py-3 font-heading uppercase tracking-wide text-xs text-forest">
                  {c.label}
                </th>
              ))}
              <th className="px-4 py-3 text-right font-heading uppercase tracking-wide text-xs text-forest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`admin-row-${endpoint}-${item.id}`} className="border-t border-border hover:bg-bone/40">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-top">
                    {c.key === "active" ? (
                      <span className={item.active ? "text-green-600 font-bold" : "text-muted-foreground"}>
                        {item.active ? "Actif" : "Masqué"}
                      </span>
                    ) : c.render ? (
                      c.render(item)
                    ) : (
                      <span className="line-clamp-2">{String(item[c.key] ?? "")}</span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEdit(item)}
                    data-testid={`admin-edit-${endpoint}-${item.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 text-forest hover:text-burnt"
                    title="Modifier"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(item.id)}
                    data-testid={`admin-delete-${endpoint}-${item.id}`}
                    className="inline-flex items-center justify-center h-8 w-8 text-red-500 hover:text-red-700"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-muted-foreground">
                  Aucun élément.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid={`admin-form-${endpoint}`}>
          <DialogHeader>
            <DialogTitle className="hd-italic text-2xl text-forest">
              {editingId ? "Modifier" : "Ajouter"} — {title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Renseignez les champs en français et en anglais. Les modifications sont visibles immédiatement sur le site.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {fields.map((fl) => (
              <div key={fl.key} className={fl.full ? "md:col-span-2" : ""}>
                <label className="block text-sm font-semibold text-forest mb-1.5">{fl.label}</label>
                {fl.type === "textarea" ? (
                  <textarea
                    data-testid={`admin-field-${endpoint}-${fl.key}`}
                    value={form[fl.key]}
                    onChange={(e) => setVal(fl.key, e.target.value)}
                    className="w-full border-2 border-border focus:border-burnt outline-none p-3 text-sm min-h-[90px] resize-none"
                  />
                ) : fl.type === "select" ? (
                  <select
                    data-testid={`admin-field-${endpoint}-${fl.key}`}
                    value={form[fl.key]}
                    onChange={(e) => setVal(fl.key, e.target.value)}
                    className="w-full border-2 border-border focus:border-burnt outline-none p-3 text-sm h-12 bg-white"
                  >
                    {fl.options.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : fl.type === "checkbox" ? (
                  <label className="flex items-center gap-2 h-12 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`admin-field-${endpoint}-${fl.key}`}
                      checked={!!form[fl.key]}
                      onChange={(e) => setVal(fl.key, e.target.checked)}
                      className="h-5 w-5 accent-burnt"
                    />
                    <span className="text-sm text-muted-foreground">{fl.hint || "Oui"}</span>
                  </label>
                ) : (
                  <input
                    type={fl.type === "number" ? "number" : "text"}
                    step={fl.type === "number" ? (fl.step || "any") : undefined}
                    data-testid={`admin-field-${endpoint}-${fl.key}`}
                    value={form[fl.key]}
                    onChange={(e) => setVal(fl.key, e.target.value)}
                    className="w-full border-2 border-border focus:border-burnt outline-none p-3 text-sm h-12"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setOpen(false)} className="btn-secondary">
              <X size={16} /> Annuler
            </button>
            <button onClick={save} disabled={saving} data-testid={`admin-save-${endpoint}`} className="btn-primary disabled:opacity-60">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntityManager;
